import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  Polygon,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import type { LeafletMouseEvent, Map as LeafletMap } from 'leaflet';
import {
  KAART_STATUSSEN,
  statusInLaag,
  type Kaartlaag,
  type KaartStatus,
  type Woning,
} from '@/types/domain';
import { BASEMAPS, getConfig } from '@/config/appConfig';
import {
  PLANGEBIED_LATLNGS,
  PLANGEBIED_MIDDEN,
  ligtInPlangebied,
} from '@/data/plangebied';
import { useApp } from '@/state/AppContext';
import { useGeolocation } from '@/hooks/useGeolocation';
import { LagenIcoon, LocatieIcoon, PuntIcoon } from '@/components/layout/Iconen';
import { Spinner } from '@/components/ui/Feedback';
import { isHuurwoning } from '@/data/huurwoningen';
import { Legenda, type SoortFilter } from './Legenda';
import { WoningLaag, type KaartPunt } from './WoningLaag';
import { Zoekbalk } from './Zoekbalk';
import './map.css';

/** Houdt het zoomniveau bij; daarmee schalen de bolletjes mee. */
function ZoomVolger({ bijZoom }: { bijZoom: (zoom: number) => void }): null {
  const kaart = useMapEvents({
    zoomend: () => bijZoom(kaart.getZoom()),
  });

  useEffect(() => {
    bijZoom(kaart.getZoom());
  }, [kaart, bijZoom]);

  return null;
}

/** Vangt een klik op de kaart op, voor het plaatsen van een handmatig punt. */
function KlikVanger({ bijKlik }: { bijKlik: (event: LeafletMouseEvent) => void }): null {
  useMapEvents({ click: bijKlik });
  return null;
}

/**
 * Houdt de kaartgrootte gelijk aan de container.
 *
 * Leaflet meet de container bij initialisatie. In een flex/absolute-layout is
 * die op dat moment nog 0x0, waardoor er maar een tegel laadt. Een
 * ResizeObserver corrigeert dat direct en vangt later ook het openen van het
 * zijpaneel en schermrotatie af.
 */
function KaartFormaat(): null {
  const kaart = useMap();

  useEffect(() => {
    const hermeet = () => kaart.invalidateSize({ animate: false });

    kaart.whenReady(hermeet);
    const frame = requestAnimationFrame(hermeet);

    const observer = new ResizeObserver(hermeet);
    observer.observe(kaart.getContainer());

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [kaart]);

  return null;
}

/** Geeft de Leaflet-instantie door aan de bovenliggende component. */
function KaartReferentie({ bijGereed }: { bijGereed: (kaart: LeafletMap) => void }): null {
  const kaart = useMap();
  useEffect(() => {
    bijGereed(kaart);
  }, [kaart, bijGereed]);
  return null;
}

export function Kaart(): JSX.Element {
  const {
    woningen,
    registratiePerBagId,
    ladenWoningen,
    selecteerWoning,
    geselecteerdeWoning,
    plaatsPunt,
    toonMelding,
    alleenLezen,
    registraties,
    openRegistratie,
  } = useApp();

  const config = getConfig();
  const kaartRef = useRef<LeafletMap | null>(null);
  const [zoom, setZoom] = useState(config.map.zoom);
  const [basemapId, setBasemapId] = useState(BASEMAPS[0].id);
  /*
   * Wat de kleur laat zien. Start op de bewoner, want dat is waar een coach
   * in het veld op stuurt: wie moet ik nog spreken. De afkoppelweergave is
   * er voor de vraag hoe het project ervoor staat.
   */
  const [laag, setLaag] = useState<Kaartlaag>('afkoppelen');
  const [soort, setSoort] = useState<SoortFilter>({ huur: true, koop: true });

  const [zichtbareStatussen, setZichtbareStatussen] = useState<Set<KaartStatus>>(
    () => new Set(KAART_STATUSSEN)
  );
  const [puntModus, setPuntModus] = useState(false);

  const gps = useGeolocation();
  const basemap = BASEMAPS.find((laag) => laag.id === basemapId) ?? BASEMAPS[0];

  /** Vliegt naar een woning en opent meteen het formulier. */
  const gaNaarWoning = useCallback(
    (woning: Woning) => {
      kaartRef.current?.flyTo([woning.latitude, woning.longitude], Math.max(zoom, 19), {
        duration: 0.6,
      });
      selecteerWoning(woning);
    },
    [selecteerWoning, zoom]
  );

  // GPS-positie gevonden: kaart erheen bewegen.
  useEffect(() => {
    if (gps.positie && kaartRef.current) {
      kaartRef.current.flyTo([gps.positie.latitude, gps.positie.longitude], 19, {
        duration: 0.8,
      });
    }
  }, [gps.positie]);

  const bijKaartKlik = useCallback(
    (event: LeafletMouseEvent) => {
      if (!puntModus) return;

      const { lat, lng } = event.latlng;

      // Bewust geen grens: een adres net buiten het plangebied kan evengoed
      // relevant zijn. Buiten de grens komt er wel een melding, zodat het een
      // bewuste keuze blijft en geen misklik.
      if (!ligtInPlangebied(lat, lng)) {
        toonMelding('info', 'Punt geplaatst buiten het plangebied.');
      }

      setPuntModus(false);
      void plaatsPunt(lat, lng);
    },
    [puntModus, plaatsPunt, toonMelding]
  );

  const punten = useMemo<KaartPunt[]>(() => {
    const uitBag = woningen.map((woning) => ({
      sleutel: woning.bagId,
      woning,
      status: statusInLaag(
        registratiePerBagId.get(woning.bagId),
        isHuurwoning(woning.adres),
        laag
      ),
      handmatig: false,
      huur: isHuurwoning(woning.adres),
      registratie: registratiePerBagId.get(woning.bagId),
    }));

    // Handmatige punten hebben geen BAG-id en zitten dus niet in de BAG-laag.
    // Zonder deze regel zouden ze na opslaan van de kaart verdwijnen.
    const handmatig = registraties
      .filter((registratie) => !registratie.bagId)
      .map((registratie) => ({
        sleutel: registratie.id,
        woning: {
          bagId: '',
          adres: registratie.adres,
          latitude: registratie.latitude,
          longitude: registratie.longitude,
        },
        status: statusInLaag(registratie, isHuurwoning(registratie.adres), laag),
        handmatig: true,
        huur: isHuurwoning(registratie.adres),
        registratie,
      }));

    return [...uitBag, ...handmatig];
  }, [woningen, registratiePerBagId, registraties, laag]);

  const bijPuntKlik = useCallback(
    (punt: KaartPunt) => {
      if (!punt.handmatig) {
        selecteerWoning(punt.woning);
        return;
      }
      // Handmatig punt: de bijbehorende registratie openen, niet een nieuwe maken.
      const registratie = registraties.find((r) => r.id === punt.sleutel);
      if (registratie) openRegistratie(registratie);
    },
    [selecteerWoning, registraties, openRegistratie]
  );

  const aantalHuur = useMemo(() => punten.filter((punt) => punt.huur).length, [punten]);
  const aantalKoop = useMemo(() => punten.filter((punt) => !punt.huur).length, [punten]);

  const aantallenPerStatus = useMemo(() => {
    const aantallen = Object.fromEntries(KAART_STATUSSEN.map((s) => [s, 0])) as Record<
      KaartStatus,
      number
    >;
    for (const punt of punten) {
      aantallen[punt.status] += 1;
    }
    return aantallen;
  }, [punten]);

  const schakelSoort = useCallback((welk: keyof SoortFilter) => {
    setSoort((huidig) => ({ ...huidig, [welk]: !huidig[welk] }));
  }, []);

  const schakelStatus = useCallback((status: KaartStatus) => {
    setZichtbareStatussen((huidig) => {
      const nieuw = new Set(huidig);
      if (nieuw.has(status)) nieuw.delete(status);
      else nieuw.add(status);
      return nieuw;
    });
  }, []);

  return (
    <div className={`kaartpagina${puntModus ? ' kaartpagina--puntmodus' : ''}`}>
      <MapContainer
        className="kaartpagina__kaart"
        center={PLANGEBIED_MIDDEN}
        zoom={config.map.zoom}
        minZoom={config.map.minZoom}
        maxZoom={config.map.maxZoom}
        zoomControl={false}
        // Canvas-renderer: honderden cirkels blijven zo soepel op mobiel.
        preferCanvas
      >
        <TileLayer
          key={basemap.id}
          url={basemap.url}
          attribution={basemap.attribution}
          maxNativeZoom={basemap.maxNativeZoom}
          maxZoom={config.map.maxZoom}
        />

        <KaartFormaat />
        <KaartReferentie
          bijGereed={(kaart) => {
            kaartRef.current = kaart;
          }}
        />
        <ZoomVolger bijZoom={setZoom} />
        <KlikVanger bijKlik={bijKaartKlik} />

        {/* Grens van het projectgebied, zodat een coach ziet waar het ophoudt. */}
        <Polygon
          positions={PLANGEBIED_LATLNGS}
          pathOptions={{
            color: '#ffffff',
            weight: 3,
            opacity: 0.9,
            fill: false,
            dashArray: '8 6',
          }}
          interactive={false}
        />

        <WoningLaag
          punten={punten}
          geselecteerdeSleutel={geselecteerdeWoning?.bagId || null}
          zichtbareStatussen={zichtbareStatussen}
          soort={soort}
          zoom={zoom}
          bijKlik={bijPuntKlik}
        />

        {/* De coach zelf. Bewust geen gevulde stip: dat zou een zevende
            status lijken tussen de woningen. Een holle ring met een schijf
            eromheen leest als "hier sta ik", niet als "dit is een huis". */}
        {gps.positie && (
          <CircleMarker
            center={[gps.positie.latitude, gps.positie.longitude]}
            radius={32}
            pathOptions={{
              color: 'rgba(255,255,255,.55)',
              weight: 1,
              fillColor: '#0b1130',
              fillOpacity: 0.18,
              interactive: false,
            }}
          />
        )}
        {gps.positie && (
          <CircleMarker
            center={[gps.positie.latitude, gps.positie.longitude]}
            radius={9}
            className="gpsring"
            pathOptions={{
              color: '#0b1130',
              weight: 3,
              fillColor: 'rgba(255,255,255,.95)',
              fillOpacity: 1,
              interactive: false,
            }}
          />
        )}
      </MapContainer>

      <Zoekbalk bijKeuze={gaNaarWoning} />

      <div className="kaartknoppen">
        <button
          type="button"
          className={`kaartknop${gps.bezig ? ' kaartknop--actief' : ''}`}
          onClick={gps.bepaal}
          aria-label="Toon mijn huidige locatie"
          title="Mijn locatie"
        >
          {gps.bezig ? <Spinner /> : <LocatieIcoon />}
        </button>

        {!alleenLezen && (
          <button
            type="button"
            className={`kaartknop${puntModus ? ' kaartknop--actief' : ''}`}
            onClick={() => setPuntModus((huidig) => !huidig)}
            aria-pressed={puntModus}
            aria-label="Zelf een punt plaatsen"
            title="Zelf een punt plaatsen"
          >
            <PuntIcoon />
          </button>
        )}

        <button
          type="button"
          className="kaartknop"
          onClick={() =>
            setBasemapId((huidig) =>
              huidig === BASEMAPS[0].id ? BASEMAPS[1].id : BASEMAPS[0].id
            )
          }
          aria-label={`Wissel naar ${basemap.id === BASEMAPS[0].id ? 'topografie' : 'luchtfoto'}`}
          title="Achtergrondkaart wisselen"
        >
          <LagenIcoon />
        </button>
      </div>

      <Legenda
        aantallen={aantallenPerStatus}
        zichtbaar={zichtbareStatussen}
        bijSchakel={schakelStatus}
        laag={laag}
        bijLaag={setLaag}
        soort={soort}
        bijSoort={schakelSoort}
        aantalHuur={aantalHuur}
        aantalKoop={aantalKoop}
      />

      {(puntModus || ladenWoningen || gps.fout) && (
        <div className="kaartstatus">
          {puntModus ? (
            'Tik op de kaart om een punt te plaatsen'
          ) : gps.fout ? (
            gps.fout
          ) : (
            <>
              <Spinner /> Woningen laden...
            </>
          )}
        </div>
      )}
    </div>
  );
}
